import { observer } from "mobx-react-lite";
import { Box, Paper, Typography } from "@mui/material";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { ServiceKey } from "./OrderProcessSummary";

interface Props {
  // The services currently ticked in the selector (upload step, or a staged Redo).
  selection: ServiceKey[];
}

/**
 * An example frame for the chosen services, shown beside the upload step's drop zone. What each
 * service does is described in the page header; this only illustrates the selection.
 */
export default observer(function ServiceOverviewPanel({ selection }: Props) {
  // Nothing ticked means nothing to illustrate.
  if (selection.length === 0) return null;

  const withSegmentation = selection.includes("segmentation");
  // Video Analysis without Inspection Insights has no burned-in OCR data to show, so it gets
  // the AI detections frame instead - with the damage outlines when Segmentation is added.
  const analysisOnly = selection.includes("analysis") && !selection.includes("ocr");
  const image = analysisOnly
    ? withSegmentation
      ? { src: "/images/VA_SEG.png", altKey: "ocrService.info.imageAnalysisSegmentation" }
      : { src: "/images/VA.png", altKey: "ocrService.info.imageAnalysis" }
    : withSegmentation
      ? selection.includes("analysis")
        ? { src: "/images/OCR_VA_SEG.png", altKey: "ocrService.info.imageOcrAnalysisSegmentation" }
        : { src: "/images/OCR_SEG.png", altKey: "ocrService.info.imageSegmentation" }
      : selection.includes("analysis")
        ? { src: "/images/OCR_VA.png", altKey: "ocrService.info.imageOcrAnalysis" }
        : { src: "/images/OCR.png", altKey: "ocrService.info.imageOcr" };

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Box
        component="img"
        src={image.src}
        alt={translationService.translate(image.altKey)}
        // Capped at about the drop zone's height; scaled down whole (not cropped), since the
        // burned-in data sits in the frame's corners.
        sx={{ maxHeight: 200, maxWidth: "100%", width: "auto", borderRadius: 1, display: "block", mx: "auto" }}
      />
      {/* One line on what the chosen process does with a frame like this one. */}
      <Typography variant="body2" color="GrayText" sx={{ mt: 1, textAlign: "center" }}>
        {translationService.translate(`${image.altKey}.caption`)}
      </Typography>
    </Paper>
  );
});
